import { useCallback, useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext, useLocation, useNavigate, useParams } from "react-router-dom";
import { ProjectFullPage } from "./ProjectFullPage.jsx";
import { buildProjectUpdatesUrl, buildProjectUrl } from "../../services/buildUrl.js";
import { guardRouterNavigator, registerNavigationGuard, syncNavigationIndex } from "./recordNavigationGuard.js";
import { showToast } from "../../components/toastService.jsx";

/** Routing only: reuse the existing secondary reader without redesigning it. */
export function ProjectFullReaderRoute({ currentRole = "viewer" }) {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const editor = useRef({ editing: false, busy: false });
  const reportEditor = useCallback(value => { editor.current = value; }, []);
  const origin = location.state?.projectFullReturn;
  const backHref = origin?.to?.startsWith("/") && !origin.to.startsWith("//")
    ? origin.to : buildProjectUrl(projectId, location.search);
  useEffect(() => guardRouterNavigator(navigator), [navigator]);
  useEffect(() => { syncNavigationIndex(); }, [location.key]);
  useEffect(() => {
    const warn = event => {
      if (editor.current?.editing || editor.current?.busy) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
  useEffect(() => registerNavigationGuard({
    isBlocking: () => Boolean(editor.current?.editing || editor.current?.busy),
    onBlocked: (proceed) => {
      if (editor.current?.busy) { showToast("A record change is still saving. Wait for it to finish before leaving.", { type: "error" }); return; }
      if (window.confirm("Discard unsaved project changes and leave?")) {
        editor.current = { editing: false, busy: false };
        proceed();
      }
    },
  }), [projectId]);
  return <ProjectFullPage key={projectId} projectId={projectId} currentRole={currentRole}
    backHref={backHref} onBack={() => navigate(backHref, { state: origin?.state ?? null })}
    onEditorStateChange={reportEditor}
    onOpenWeeklyArchive={() => navigate(buildProjectUpdatesUrl(projectId, location.search))} />;
}
